// Extracted from original/src/App.tsx:28643-30206 (`activeTab === "movements"`, ADR-08 §2.1 "copy, then cut logic"):
//   mode buttons (journal / by employee)                     :28651-28672
//   journal filters: search, type, from / to, hour, reset    :28675-28797
//   share / save PDF / print matrix                          :28798-29133
//   bulk selection bar                                       :29139-29194
//   table (head, rows, row actions)                          :29196-29500
//   pagination                                               :29504-29567
//   employee mode: filters + export matrix                   :29572-29999
//   employee summary cards                                   :30000-30054
//   employee table                                           :30055-30206
// Kept: every class string, icon and size, the motion frame, the sortable headers with their arrows, the "#" code
// cell (old `getUnifiedEntryCode`: employee code + number), the "آجل" badge with the customer name, the blue italic
// note line, the old in / out / commission colours, the date cell "MM-DD-YYYY HH:mm", the row checkbox and the
// selected-row tint, the page-size select (10 / 20 / 50 / 100) and the pager shown only above one page.
// Cut: `movements` from `getMovementsPaginated` (stored rows, virtual expense ids, merged legs — D06 R-2, R-3) →
// `rows` from the ledger read model `GET /journal` (D-091); `getMovementEffectiveInOut` / `formatNumber` /
// `calculateTotalCommInIQD` / `new Date().toLocale…` → preformatted labels (office time zone, D-096);
// `toggleSort` / `setCurrentPage` / `setPageSize` / `setSelectedMovementIds` → callbacks; the three export buttons'
// inline report builders (:28798-29133, the visible page only — D06 R-9) → `onShare` / `onSavePdf` / `onPrint` (the
// container builds the whole filtered set, D-095); `handleDeleteMovement` / `handleBulkDeleteMovements` →
// `onCancel` / `onBulkCancel` (reason → PIN → reversal entries, D-091, D-094); `hasPermission(currentUser, …)` →
// `canEdit` / `canCancel` / `showEmployeeMode`; the product-sale print button (:29397-29437, store add-on after G1p).
// Changed by decision: the bulk button is shown to `delete_movements` holders only (D06 R-8 → D-094); an edited
// operation shows a "معدّل" badge beside the deferred badge (D-013 "marked edited"); a correction waiting for
// second-person approval shows "⏳" and has no actions (D-035, V-41); the default date range is the office's today
// (D-096; old: UTC today).
// Not ported: the unreachable `audit` / `customer_audit` modes (D06 R-10, D-096).
// Added: data-testid attributes.
import { motion } from 'motion/react';
import {
  Activity, ArrowDown, ArrowDownLeft, ArrowUp, ArrowUpDown, ArrowUpRight, CheckSquare, ChevronLeft, ChevronRight, DollarSign, Edit2, Eye, FileText, History,
  Printer, RotateCcw, Search, Share2, Trash2, Users, X,
} from 'lucide-react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';

export type JournalMode = 'journal' | 'employee';
export type JournalSortField = 'item' | 'c_in' | 'c_out' | 'comm' | 'created_at';
export type JournalTypeFilter = 'all' | 'deposit' | 'withdraw' | 'transfer' | 'product' | 'debt' | 'installment' | 'employee' | 'expense' | 'manual_work';
export const JOURNAL_TYPE_FILTERS: readonly JournalTypeFilter[] = ['all', 'deposit', 'withdraw', 'transfer', 'product', 'debt', 'installment', 'employee', 'expense', 'manual_work'];
/** The employee mode's type list (old :29597-29606: no installments, no employee). */
export const JOURNAL_EMP_TYPE_FILTERS: readonly JournalTypeFilter[] = ['all', 'deposit', 'withdraw', 'transfer', 'product', 'debt', 'expense', 'manual_work'];

export interface JournalRowView {
  readonly id: string;
  /** "SM-12" (old `getUnifiedEntryCode`); the cell adds the "#". */
  readonly code: string;
  readonly item: string;
  readonly deferred: boolean;
  readonly partyName: string | null;
  readonly note: string | null;
  readonly boxName: string | null;
  readonly operator: string | null;
  readonly costLabel: string;
  readonly saleLabel: string;
  /** "3,003,000" or "-". */
  readonly inLabel: string;
  readonly outLabel: string;
  readonly commLabel: string;
  /** "09-29-2026 08:57" (office time zone). */
  readonly dateLabel: string;
  readonly edited: boolean;
  /** A correction of this operation waits for approval (D-035): no actions. */
  readonly pending: 'journal_cancel' | 'journal_edit' | null;
}

export interface JournalEmployeeStats {
  readonly count: string;
  readonly inLabel: string;
  readonly outLabel: string;
  readonly commLabel: string;
}

export interface JournalProps {
  readonly mode: JournalMode;
  readonly onModeChange: (m: JournalMode) => void;
  readonly showEmployeeMode: boolean;
  // journal filters
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly typeFilter: JournalTypeFilter;
  readonly onTypeFilterChange: (v: JournalTypeFilter) => void;
  readonly startDate: string;
  readonly onStartDateChange: (v: string) => void;
  readonly endDate: string;
  readonly onEndDateChange: (v: string) => void;
  readonly hourFilter: string;
  readonly onHourFilterChange: (v: string) => void;
  readonly onReset: () => void;
  readonly onShare: () => void;
  readonly onSavePdf: () => void;
  readonly onPrint: () => void;
  // table
  readonly rows: readonly JournalRowView[];
  readonly selectedIds: readonly string[];
  readonly onSelectionChange: (ids: string[]) => void;
  readonly sortField: JournalSortField;
  readonly sortOrder: 'asc' | 'desc';
  readonly onSort: (f: JournalSortField) => void;
  readonly canEdit: boolean;
  readonly canCancel: boolean;
  readonly onDetails: (id: string) => void;
  readonly onEdit: (id: string) => void;
  readonly onCancel: (id: string) => void;
  readonly onBulkCancel: () => void;
  readonly page: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly onPageChange: (p: number) => void;
  readonly onPageSizeChange: (n: number) => void;
  // employee mode
  readonly employeeOptions: readonly SelectOption[];
  readonly selectedEmployee: string;
  readonly onEmployeeChange: (v: string) => void;
  readonly empTypeFilter: JournalTypeFilter;
  readonly onEmpTypeFilterChange: (v: JournalTypeFilter) => void;
  readonly empStartDate: string;
  readonly onEmpStartDateChange: (v: string) => void;
  readonly empEndDate: string;
  readonly onEmpEndDateChange: (v: string) => void;
  readonly onEmpReset: () => void;
  readonly onEmpShare: () => void;
  readonly onEmpSavePdf: () => void;
  readonly onEmpPrint: () => void;
  readonly empStats: JournalEmployeeStats;
  readonly empRows: readonly JournalRowView[];
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const hourLabel = (h: string): string => {
  const n = Number(h);
  const suffix = n < 12 ? 'AM' : 'PM';
  const twelve = n % 12 === 0 ? 12 : n % 12;
  return `${h}:00 (${twelve} ${suffix})`;
};

export function Journal(props: JournalProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const movements = props.rows;
  const selectedMovementIds = props.selectedIds;
  const movementsMode = props.mode;
  const sortField = props.sortField;
  const sortOrder = props.sortOrder;
  const isSelected = (id: string) => selectedMovementIds.includes(id);
  const allVisibleSelected = movements.length > 0 && movements.every((m) => isSelected(m.id));
  const sortIcon = (f: JournalSortField) =>
    sortField === f ? (sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} />;
  const typeOptions = (list: readonly JournalTypeFilter[]): SelectOption[] => list.map((id) => ({ id, label: t(`jr.type_${id}`) }));

  return (
    <motion.div
      key="movements"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
      data-testid="journal-screen"
    >
      {props.showEmployeeMode && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => props.onModeChange('journal')}
            className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${movementsMode === 'journal' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white dark:bg-[#0d1117] text-gray-500 dark:text-[#8b949e] border border-gray-100 dark:border-[#30363d]'}`}
            data-testid="journal-mode-journal"
          >
            <History size={18} />
            {t('jr.mode_journal')}
          </button>
          <button
            onClick={() => props.onModeChange('employee')}
            className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${movementsMode === 'employee' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white dark:bg-[#0d1117] text-gray-500 dark:text-[#8b949e] border border-gray-100 dark:border-[#30363d]'}`}
            data-testid="journal-mode-employee"
          >
            <Users size={18} />
            {t('jr.mode_employees')}
          </button>
        </div>
      )}
      {movementsMode === 'journal' || !props.showEmployeeMode ? (
        <>
          {/* Filters */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('jr.search_label')}
              </label>
              <div className="relative">
                <Search
                  className={`absolute ${lang === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#8b949e]`}
                  size={18}
                />
                <input
                  type="text"
                  value={props.search}
                  onChange={(e) => props.onSearchChange(e.target.value)}
                  placeholder={t('search')}
                  className={`w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all`}
                  data-testid="journal-search"
                />
                {props.search && (
                  <button
                    onClick={() => props.onSearchChange('')}
                    className={`absolute ${lang === 'ar' ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all`}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="w-full sm:w-52 flex-1 min-w-[150px]">
              <CustomSelect
                label={t('jr.type_label')}
                options={typeOptions(JOURNAL_TYPE_FILTERS)}
                value={props.typeFilter}
                onChange={(val) => props.onTypeFilterChange(val as JournalTypeFilter)}
                headerTitle={t('jr.type_header')}
                searchable={false}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <label className="block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('jr.from_date')}
              </label>
              <EnglishDatePicker value={props.startDate} onChange={(val) => props.onStartDateChange(val)} className="!py-2" />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <label className="block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">
                {t('jr.to_date')}
              </label>
              <EnglishDatePicker value={props.endDate} onChange={(val) => props.onEndDateChange(val)} className="!py-2" />
            </div>
            <div className="w-full sm:w-44 flex-1 min-w-[130px]">
              <CustomSelect
                label={t('jr.hour')}
                options={[{ id: 'all', label: t('jr.hour_all') }, ...HOURS.map((h) => ({ id: h, label: hourLabel(h) }))]}
                value={props.hourFilter}
                onChange={(val) => props.onHourFilterChange(String(val))}
                headerTitle={t('jr.hour_header')}
                searchable={false}
                size="sm"
              />
            </div>
            <button
              onClick={props.onReset}
              className="px-4 py-2 text-gray-500 dark:text-[#8b949e] hover:text-gray-700 dark:hover:text-[#f0f6fc] font-bold transition-colors cursor-pointer"
              data-testid="journal-reset"
            >
              {t('reset')}
            </button>
            {/* Fast Export/Share Buttons Matrix */}
            <div className="flex items-center gap-4 sm:gap-6 px-4 py-1.5 bg-emerald-50/80 dark:bg-[#161b22] border border-emerald-200/80 dark:border-[#30363d] rounded-2xl sm:rounded-full shadow-xs">
              {/* Share on WhatsApp */}
              <button
                type="button"
                onClick={props.onShare}
                className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                title={t('jr.share_title')}
                data-testid="journal-share"
              >
                <Share2 size={19} className="stroke-[1.8]" />
              </button>
              {/* Save PDF to Desktop */}
              <button
                type="button"
                onClick={props.onSavePdf}
                className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                title={t('jr.pdf_title')}
                data-testid="journal-pdf"
              >
                <FileText size={19} className="stroke-[1.8]" />
              </button>
              {/* Direct Print */}
              <button
                type="button"
                onClick={props.onPrint}
                className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                title={t('jr.print_title')}
                data-testid="journal-print"
              >
                <Printer size={19} className="stroke-[1.8]" />
              </button>
            </div>
          </div>

          {/* Bulk Selection and Batch Delete Action Bar */}
          <div className="bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] p-4 flex flex-wrap items-center justify-between gap-4 dark:digital-border">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const visibleIds = movements.map((m) => m.id);
                  if (allVisibleSelected) props.onSelectionChange(selectedMovementIds.filter((id) => !visibleIds.includes(id)));
                  else props.onSelectionChange(Array.from(new Set([...selectedMovementIds, ...visibleIds])));
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] text-gray-700 dark:text-[#f0f6fc] flex items-center gap-2 transition-all cursor-pointer"
                data-testid="journal-select-visible"
              >
                <CheckSquare size={16} className="text-blue-500" />
                {allVisibleSelected ? t('jr.deselect_visible') : t('jr.select_visible', { n: movements.length })}
              </button>

              {selectedMovementIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => props.onSelectionChange([])}
                  className="px-3 py-2 rounded-xl text-xs font-bold text-gray-500 hover:text-gray-700 dark:text-[#8b949e] dark:hover:text-white transition-colors cursor-pointer"
                >
                  {t('jr.clear_selection')}
                </button>
              )}

              <span className="text-xs font-bold text-gray-500 dark:text-[#8b949e]">{t('jr.selected', { n: selectedMovementIds.length })}</span>
            </div>

            {props.canCancel && selectedMovementIds.length > 0 && (
              <button
                type="button"
                onClick={props.onBulkCancel}
                className="px-5 py-2.5 rounded-xl text-xs font-black bg-rose-600 hover:bg-rose-700 active:scale-95 text-white shadow-lg shadow-rose-600/20 flex items-center gap-2 transition-all cursor-pointer"
                data-testid="journal-bulk-cancel"
              >
                <Trash2 size={16} />
                {t('jr.delete_selected', { n: selectedMovementIds.length })}
              </button>
            )}
          </div>

          <div className="bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] p-6 dark:digital-border">
            <div className="overflow-x-auto">
              <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`} data-testid="journal-table">
                <thead>
                  <tr className="text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px] w-10 text-center">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => {
                          const visibleIds = movements.map((m) => m.id);
                          if (e.target.checked) props.onSelectionChange(Array.from(new Set([...selectedMovementIds, ...visibleIds])));
                          else props.onSelectionChange(selectedMovementIds.filter((id) => !visibleIds.includes(id)));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                      />
                    </th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">#</th>
                    <th
                      className="pb-4 font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => props.onSort('item')}
                    >
                      <div className="flex items-center gap-1">
                        {t('details')}
                        {sortIcon('item')}
                      </div>
                    </th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('jr.col_wallet')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('jr.col_operator')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('costPrice')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('salePrice')}</th>
                    <th
                      className="pb-4 font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => props.onSort('c_in')}
                    >
                      <div className="flex items-center gap-1">
                        {t('in')}
                        {sortIcon('c_in')}
                      </div>
                    </th>
                    <th
                      className="pb-4 font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => props.onSort('c_out')}
                    >
                      <div className="flex items-center gap-1">
                        {t('out')}
                        {sortIcon('c_out')}
                      </div>
                    </th>
                    <th
                      className="pb-4 font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => props.onSort('comm')}
                    >
                      <div className="flex items-center gap-1">
                        {t('comm')}
                        {sortIcon('comm')}
                      </div>
                    </th>
                    <th
                      className="pb-4 font-bold uppercase tracking-widest text-[10px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => props.onSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        {t('date')}
                        {sortIcon('created_at')}
                      </div>
                    </th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('jr.col_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr
                      key={m.id}
                      className={`border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group ${isSelected(m.id) ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
                      data-testid={`journal-row-${m.code}`}
                    >
                      <td className="py-4 text-center w-10">
                        <input
                          type="checkbox"
                          checked={isSelected(m.id)}
                          disabled={m.pending !== null}
                          onChange={(e) => {
                            if (e.target.checked) props.onSelectionChange([...selectedMovementIds, m.id]);
                            else props.onSelectionChange(selectedMovementIds.filter((id) => id !== m.id));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                        />
                      </td>
                      <td className="py-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">#{m.code}</td>
                      <td className="py-4 font-black dark:text-[#f0f6fc]">
                        <div className="flex flex-col">
                          <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{m.item}</span>
                          {(m.deferred || m.edited || m.pending) && (
                            <div className="flex items-center gap-1 mt-1">
                              {m.deferred && (
                                <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">
                                  {t('jr.deferred')}
                                </span>
                              )}
                              {m.deferred && m.partyName && <span className="text-[10px] text-gray-400 dark:text-[#8b949e] font-bold">({m.partyName})</span>}
                              {m.edited && (
                                <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter" data-testid="journal-edited-badge">
                                  {t('jr.edited')}
                                </span>
                              )}
                              {m.pending && (
                                <span className="text-[10px] bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded font-black">
                                  {t(m.pending === 'journal_cancel' ? 'jr.pending_cancel' : 'jr.pending_edit')}
                                </span>
                              )}
                            </div>
                          )}
                          {m.note && <span className="text-[11px] text-blue-500 dark:text-blue-400 italic mt-0.5">{m.note}</span>}
                        </div>
                      </td>
                      <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-bold">{m.boxName || t('jr.vault')}</td>
                      <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-black">{m.operator || t('jr.unknown')}</td>
                      <td className="py-4 text-gray-400 dark:text-[#8b949e] font-mono text-sm">{m.costLabel}</td>
                      <td className="py-4 text-gray-400 dark:text-[#8b949e] font-mono text-sm">{m.saleLabel}</td>
                      <td className="py-4 text-emerald-600 dark:text-emerald-400 font-black font-mono dark:digital-glow">{m.inLabel}</td>
                      <td className="py-4 text-rose-600 dark:text-rose-400 font-black font-mono dark:digital-glow">{m.outLabel}</td>
                      <td className="py-4 font-black dark:text-[#f0f6fc] font-mono dark:digital-glow text-blue-600 dark:text-blue-400">{m.commLabel}</td>
                      <td className="py-4 text-gray-400 dark:text-[#8b949e] text-xs font-bold font-mono">{m.dateLabel}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => props.onDetails(m.id)}
                            className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors cursor-pointer"
                            title={t('jr.details_title')}
                            data-testid={`journal-details-${m.code}`}
                          >
                            <Eye size={14} />
                          </button>
                          {props.canEdit && m.pending === null && (
                            <button
                              onClick={() => props.onEdit(m.id)}
                              className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title={t('edit')}
                              data-testid={`journal-edit-${m.code}`}
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {props.canCancel && m.pending === null && (
                            <button
                              onClick={() => props.onCancel(m.id)}
                              className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                              title={t('delete')}
                              data-testid={`journal-cancel-${m.code}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {props.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-100 dark:border-[#30363d]" data-testid="journal-pager">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => props.onPageChange(Math.max(1, props.page - 1))}
                    disabled={props.page === 1}
                    className="p-2 rounded-lg border border-gray-100 dark:border-[#30363d] disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-[#161b22] transition-colors"
                  >
                    {lang === 'ar' ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                  </button>
                  <div className="flex items-center gap-1 text-sm font-bold dark:text-[#f0f6fc]">
                    <span>{t('page')}</span>
                    <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">{props.page}</span>
                    <span>{t('of')}</span>
                    <span>{props.totalPages}</span>
                  </div>
                  <button
                    onClick={() => props.onPageChange(Math.min(props.totalPages, props.page + 1))}
                    disabled={props.page === props.totalPages}
                    className="p-2 rounded-lg border border-gray-100 dark:border-[#30363d] disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-[#161b22] transition-colors"
                  >
                    {lang === 'ar' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={props.pageSize}
                    onChange={(e) => props.onPageSizeChange(Number(e.target.value))}
                    className="bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] rounded-lg px-3 py-1.5 text-sm font-bold dark:text-[#f0f6fc] outline-none focus:border-blue-500 transition-colors"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400 dark:text-[#8b949e] font-bold uppercase tracking-widest">{t('jr.per_page')}</span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {/* Employee filters */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] flex flex-wrap gap-4 items-end dark:digital-border">
            <div className="flex-1 min-w-[200px]">
              <CustomSelect
                label={t('jr.emp_select')}
                options={[{ id: '', label: t('jr.emp_all') }, ...props.employeeOptions]}
                value={props.selectedEmployee}
                onChange={(val) => props.onEmployeeChange(String(val))}
                headerTitle={t('jr.emp_header')}
                placeholder={t('jr.emp_all')}
                searchable={true}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-52 flex-1 min-w-[150px]">
              <CustomSelect
                label={t('jr.type_label')}
                options={typeOptions(JOURNAL_EMP_TYPE_FILTERS)}
                value={props.empTypeFilter}
                onChange={(val) => props.onEmpTypeFilterChange(val as JournalTypeFilter)}
                headerTitle={t('jr.emp_type_header')}
                searchable={false}
                size="sm"
              />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <label className="block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('jr.from_date')}</label>
              <EnglishDatePicker value={props.empStartDate} onChange={(val) => props.onEmpStartDateChange(val)} className="!py-2" />
            </div>
            <div className="w-full sm:w-48 flex-1 min-w-[130px]">
              <label className="block text-sm font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('jr.to_date')}</label>
              <EnglishDatePicker value={props.empEndDate} onChange={(val) => props.onEmpEndDateChange(val)} className="!py-2" />
            </div>
            <div className="flex items-center gap-3 self-end min-w-[200px] justify-end">
              {/* Reset Button */}
              <button
                onClick={props.onEmpReset}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 dark:text-[#8b949e] hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 border border-gray-200 dark:border-[#30363d] rounded-xl font-bold transition-all cursor-pointer active:scale-95"
                title={t('jr.emp_reset_title')}
              >
                <RotateCcw size={14} className="animate-spin-once" />
                <span>{t('reset')}</span>
              </button>
              {/* Fast Export/Share Buttons Matrix */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50/80 dark:bg-[#161b22] border border-emerald-200/80 dark:border-[#30363d] rounded-xl shadow-xs">
                <button
                  type="button"
                  onClick={props.onEmpShare}
                  className="p-1 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-lg transition-all cursor-pointer active:scale-90"
                  title={t('jr.share_title')}
                >
                  <Share2 size={15} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onEmpSavePdf}
                  className="p-1 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-lg transition-all cursor-pointer active:scale-90"
                  title={t('jr.pdf_title')}
                >
                  <FileText size={15} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onEmpPrint}
                  className="p-1 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-lg transition-all cursor-pointer active:scale-90"
                  title={t('jr.print_title')}
                >
                  <Printer size={15} className="stroke-[1.8]" />
                </button>
              </div>
            </div>
          </div>
          {/* Summary Statistics Bento Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] dark:digital-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-wider">{t('jr.emp_total_count')}</span>
                <Activity size={18} className="text-blue-500" />
              </div>
              <p className="text-2xl font-black dark:text-[#f0f6fc] font-mono dark:digital-glow">{props.empStats.count}</p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] dark:digital-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-wider">{t('jr.emp_total_in')}</span>
                <ArrowDownLeft size={18} className="text-emerald-500" />
              </div>
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono dark:digital-glow">
                {props.empStats.inLabel} <span className="text-xs font-bold">د.ع</span>
              </p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] dark:digital-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-wider">{t('jr.emp_total_out')}</span>
                <ArrowUpRight size={18} className="text-rose-500" />
              </div>
              <p className="text-2xl font-black text-rose-600 dark:text-rose-400 font-mono dark:digital-glow">
                {props.empStats.outLabel} <span className="text-xs font-bold">د.ع</span>
              </p>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] dark:digital-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-wider">{t('jr.emp_total_comm')}</span>
                <DollarSign size={18} className="text-purple-500" />
              </div>
              <p className="text-2xl font-black text-purple-600 dark:text-purple-400 font-mono dark:digital-glow">
                {props.empStats.commLabel} <span className="text-xs font-bold">د.ع</span>
              </p>
            </div>
          </div>
          {/* Data Table */}
          <div className="bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] p-6 dark:digital-border">
            <div className="overflow-x-auto">
              <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`} data-testid="journal-emp-table">
                <thead>
                  <tr className="text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">#</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('details')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('jr.col_wallet')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('jr.col_operator')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('in')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('out')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('comm')}</th>
                    <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">{t('date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {props.empRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-400 dark:text-[#8b949e] font-bold">
                        {t('jr.emp_none')}
                      </td>
                    </tr>
                  ) : (
                    props.empRows.map((m) => (
                      <tr key={m.id} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group">
                        <td className="py-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">#{m.code}</td>
                        <td className="py-4 font-black dark:text-[#f0f6fc]">
                          <div className="flex flex-col">
                            <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{m.item}</span>
                            {m.deferred && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">
                                  {t('jr.deferred')}
                                </span>
                                {m.partyName && <span className="text-[10px] text-gray-400 dark:text-[#8b949e] font-bold">({m.partyName})</span>}
                              </div>
                            )}
                            {m.note && <span className="text-[11px] text-blue-500 dark:text-blue-400 italic mt-0.5">{m.note}</span>}
                          </div>
                        </td>
                        <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-bold">{m.boxName || t('jr.vault')}</td>
                        <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-black">{m.operator || t('jr.unknown')}</td>
                        <td className="py-4 text-emerald-600 dark:text-emerald-400 font-black font-mono dark:digital-glow">{m.inLabel}</td>
                        <td className="py-4 text-rose-600 dark:text-rose-400 font-black font-mono dark:digital-glow">{m.outLabel}</td>
                        <td className="py-4 font-black dark:text-[#f0f6fc] font-mono dark:digital-glow text-blue-600 dark:text-blue-400">{m.commLabel}</td>
                        <td className="py-4 text-gray-400 dark:text-[#8b949e] text-xs font-bold font-mono">{m.dateLabel}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
